import { IParsedTable, parseDelimited } from '../parse-csv';
import { PREFIX_BYTES, SAMPLE_ROWS } from './constants';

/**
 * Reads just enough of a file to drive the column-mapping step. [AVP-891]
 *
 * The browser no longer parses the whole import — the server does. All the
 * wizard needs is the header and a few rows to show the user what they are
 * mapping, so it reads a fixed prefix instead of a 30 MB file.
 */

export interface IPrefixSample extends IParsedTable {
  /** true when the prefix covered the entire file, so nothing was cut */
  whole: boolean;
}

export const readFilePrefix = (file: Blob, bytes: number): Promise<string> => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => resolve(String(reader.result ?? ''));
    reader.onerror = (): void => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsText(file.slice(0, bytes), 'utf-8');
  })
);

/**
 * Parses the prefix into a header plus a handful of sample rows.
 *
 * The last row is dropped unless the prefix reached the end of the file: a
 * byte slice almost always lands mid-line, and `readAsText` turns a split
 * multi-byte character into U+FFFD. Showing the user half a row as a sample
 * would be worse than showing one row fewer.
 */
export const samplePrefix = async (
  file: Blob,
  bytes: number = PREFIX_BYTES,
  maxRows: number = SAMPLE_ROWS,
): Promise<IPrefixSample> => {
  const whole = file.size <= bytes;
  const text = await readFilePrefix(file, bytes);

  // read one extra row so that dropping the cut one still leaves maxRows
  const table = parseDelimited(text, { maxRows: maxRows + 1 });

  const rows = [...table.rows];
  const rowLines = [...table.rowLines];

  if (!whole && rows.length > 0) {
    rows.pop();
    rowLines.pop();
  }

  return {
    ...table,
    rows: rows.slice(0, maxRows),
    rowLines: rowLines.slice(0, maxRows),
    whole,
  };
};

/**
 * True when the prefix gave us too little to map against. Very long rows, or
 * an unterminated quote at the cut, can leave nothing usable — better to warn
 * than to block, because the mapping may still be right.
 */
export const sampleIsThin = (sample: IPrefixSample): boolean => (
  sample.header.length === 0 || sample.rows.length < 2
);

export default samplePrefix;
